import {
  Button,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CustomerProfileLayout, {
  DESKTOP_PROFILE_QUERY,
} from './CustomerProfileLayout';
import CustomerSummaryCard from './CustomerSummaryCard';
import CustomerProfileActions from './CustomerProfileActions';
import CustomerProfileTabs from './tabs/CustomerProfileTabs';
import useGetCustomerOverview from '../../../hooks/customers/useGetCustomerOverview';
import useCustomerActions from '../../../hooks/customers/useCustomerActions';
import useUser from '../../../hooks/global/useUser';
import { LoadedUser } from '../../../interfaces';
import FullPageModal from '../../modal/FullPageModal';
import CustomerForm from '../../form/CustomerForm';
import FormSmallModal from '../../global/FormSmallModal';

interface Props {
  customerId: string;
  onBack: () => void;
}

function CustomerProfilePage({ customerId, onBack }: Props) {
  const { user } = useUser<LoadedUser>();
  const businessId = user?.business.id ?? '';
  const isDesktop = useMediaQuery(DESKTOP_PROFILE_QUERY);

  const {
    data: customer,
    isLoading,
    isError,
    refetch,
  } = useGetCustomerOverview(customerId, { enabled: true });
  const stats = customer?.summary;

  const actions = useCustomerActions({
    customer,
    customerId,
    onCustomerChanged: refetch,
  });

  if (isLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: '50vh' }}>
        <CircularProgress color="primary" />
      </Stack>
    );
  }

  if (isError || !customer) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        gap={2}
        sx={{ height: '50vh' }}>
        <Typography variant="body1">
          There was an error loading this customer.
        </Typography>
        <Button variant="outlined" onClick={onBack}>
          Back to customers
        </Button>
      </Stack>
    );
  }

  const profileActions = (
    <CustomerProfileActions
      onBack={onBack}
      overflowActions={actions.overflowActions}
    />
  );
  const summary = (
    <CustomerSummaryCard
      customer={customer}
      stats={stats}
      isStatsLoading={false}
      businessId={businessId}
      actions={isDesktop ? profileActions : undefined}
      onEditCustomer={actions.openEditCustomerModal}
      onGuestChanged={refetch}
    />
  );

  return (
    <>
      <CustomerProfileLayout summary={summary}>
        {!isDesktop && profileActions}
        <CustomerProfileTabs
          customerId={customerId}
          businessId={businessId}
          onUploadFile={actions.openFileUploadModal}
          profile={isDesktop ? undefined : summary}
        />
      </CustomerProfileLayout>

      {actions.isEditCustomerModalOpen && (
        <FullPageModal
          modalOpen
          setModalOpen={actions.closeEditCustomerModal}
          name="Edit Customer">
          <CustomerForm
            setModalOpen={actions.closeEditCustomerModal}
            selectedCustomer={customer}
            onCustomerSave={async () => {
              await refetch();
            }}
          />
        </FullPageModal>
      )}

      {actions.isFileUploadModalOpen && (
        <FormSmallModal
          maxWidth="480px"
          form={actions.fileUploadForm}
          open
          onClose={actions.closeFileUploadModal}
        />
      )}
    </>
  );
}

export default CustomerProfilePage;
